import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { pw933bApi, QueryRequest, QueryResponse, DefaultYearMonth } from '../../../services/pw933bApi';

export interface PW933BQueryState {
  loading: boolean;
  result: QueryResponse | null;
  queried: boolean;
  noData: boolean;
  defaults: DefaultYearMonth | null;
}

export interface PW933BQueryActions {
  runQuery: (req: QueryRequest) => Promise<void>;
  reset: () => void;
  loadDefaults: () => Promise<void>;
}

export function usePW933BQuery(): PW933BQueryState & PW933BQueryActions {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<QueryResponse | null>(null);
  const [queried, setQueried] = useState(false);
  const [noData, setNoData] = useState(false);
  const [defaults, setDefaults] = useState<DefaultYearMonth | null>(null);

  const loadDefaults = useCallback(async () => {
    try {
      const resp = await pw933bApi.getDefaults();
      setDefaults(resp);
    } catch {
      // Use current date as fallback
      const now = new Date();
      setDefaults({ year: now.getFullYear(), month: now.getMonth() + 1 });
    }
  }, []);

  const runQuery = useCallback(async (req: QueryRequest) => {
    setLoading(true);
    setQueried(false);
    setNoData(false);
    try {
      const resp = await pw933bApi.query(req);
      if (resp.rows && resp.rows.length > 0) {
        setResult(resp);
        setNoData(false);
      } else {
        setResult(null);
        setNoData(true);
        message.info('查無符合條件的薪資撥款資料');
      }
      setQueried(true);
    } catch {
      message.error('查詢失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setQueried(false);
    setNoData(false);
  }, []);

  // Load defaults on mount
  useEffect(() => {
    loadDefaults();
  }, [loadDefaults]);

  return { loading, result, queried, noData, defaults, runQuery, reset, loadDefaults };
}
